import React, { useCallback, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import dynamic from 'next/dynamic';

import { Button } from '@motorway/mw-highway-code';

import VehicleListCard from 'Components/VehicleList/VehicleListCard/VehicleListCard';
import { EARLY_DOCUMENTS_EVENTS } from 'Features/EarlyDocuments';
import { PAID_LISTING_EVENTS } from 'Features/PaidListings';
import useIntersectionObserver, { useSupportsIntersectionObserver } from 'Utilities/hooks/useIntersectionObserver';
import {
	TRACKING_PAYLOAD_ATTRIBUTE_NAME,
	useTrackElementVisibleOnce,
} from 'Utilities/hooks/useTrackElementVisibleOnce';
import proPropTypes from 'Utilities/proPropTypes';

import styles from '../FilterableVehicleList.module.scss';

export const CARD_PLACEHOLDER_COUNT = 8;

const TRACK_TAG_EVENTS = {
	...EARLY_DOCUMENTS_EVENTS,
	...PAID_LISTING_EVENTS,
};

const VehiclesListResults = ({ hasMore, isLoading, loadMore, vehicles }) => {
	const sentinelRef = useRef();
	const hasMoreRef = useRef(hasMore);
	const isLoadingRef = useRef(isLoading);
	const supportsIntersectionObserver = useSupportsIntersectionObserver({
		defaultValue: true, // Hide loadMore button on SSR
	});
	const { setTrackElementRef } = useTrackElementVisibleOnce(TRACK_TAG_EVENTS);

	const getVehiclesListPlaceholders = () =>
		[...Array(CARD_PLACEHOLDER_COUNT).keys()].map((k) => (
			<li key={k} className={styles.vehicleCard}>
				<VehicleListCard loading />
			</li>
		));

	const getVehiclesListCards = () => {
		if (!vehicles) {
			return getVehiclesListPlaceholders();
		}

		return vehicles.map((vehicle) => (
			<li
				key={vehicle.id}
				className={styles.vehicleCard}
				data-testid={vehicle.id}
				{...{ [TRACKING_PAYLOAD_ATTRIBUTE_NAME]: vehicle.id }}
				{...((vehicle.isPaidListing || vehicle.hasEarlyDocuments) && {
					ref: setTrackElementRef,
				})}
			>
				<VehicleListCard vehicle={vehicle} />
			</li>
		));
	};

	const getLoadMoreButton = () => {
		if (supportsIntersectionObserver || !hasMore) {
			return null;
		}

		return (
			<div className={styles.loadMoreBox}>
				<Button fullWidth label="Load more" onClick={loadMore} />
			</div>
		);
	};

	const onIntersect = useCallback(
		(entries) => {
			// if we would use direct values, anytime one of the values below
			// would change, new IntersectionObserver instance would be recreated.
			// thanks to refs, we only initiate it once. #optimisation
			if (isLoadingRef.current || hasMoreRef.current === false) {
				return;
			}

			const { intersectionRatio, isIntersecting } = entries[0];

			if (isIntersecting === true && intersectionRatio === 1) {
				loadMore();
			}
		},
		[loadMore],
	);

	useEffect(() => {
		hasMoreRef.current = hasMore;
		isLoadingRef.current = isLoading;
	}, [hasMore, isLoading]);

	useIntersectionObserver({
		onIntersect,
		rootMargin: '0px 0px 1344px 0px', // 3 rows
		target: sentinelRef,
		threshold: 1,
	});

	return (
		<>
			<ul className={cx(styles.vehiclesListing)}>
				{getVehiclesListCards()}
				<li className={styles.sentinelBox}>
					<div ref={sentinelRef} className={styles.sentinel} />
				</li>
				{isLoading ? getVehiclesListPlaceholders() : null}
			</ul>
			{getLoadMoreButton()}
		</>
	);
};

VehiclesListResults.defaultProps = {
	vehicles: null,
};

VehiclesListResults.propTypes = {
	hasMore: PropTypes.bool.isRequired,
	isLoading: PropTypes.bool.isRequired,
	loadMore: PropTypes.func.isRequired,
	vehicles: PropTypes.arrayOf(proPropTypes.vehicleCardList),
};

export default dynamic(() => Promise.resolve(VehiclesListResults), {
	ssr: false,
});
